import { useEffect, useState } from "react";

const SHAARP_EXTENSION_ID = import.meta.env.VITE_CHROME_EXTENSION_ID;

// Extend the global Window interface to include chrome
declare global {
  interface Window {
    chrome?: {
      runtime?: {
        sendMessage: (
          extensionId: string,
          message: unknown,
          callback: (response?: unknown) => void
        ) => void;
        lastError?: { message: string };
      };
    };
  }
}

// Type for browser detection
export type BrowserType = "chrome" | "edge";

export function detectBrowser(): BrowserType {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("edg/")) {
    return "edge";
  }
  if (userAgent.includes("chrome") || userAgent.includes("chromium")) {
    return "chrome";
  }

  // Default to chrome
  return "chrome";
}

export function useChromeExtensionStatus() {
  const [isInstalled, setIsInstalled] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(true);

  useEffect(() => {
    const checkExtension = async () => {
      setIsChecking(true);

      // Check if chrome.runtime is available
      if (typeof window.chrome === "undefined" || !window.chrome.runtime) {
        setIsInstalled(false);
        setIsChecking(false);
        return;
      }

      try {
        let responseReceived = false;

        // Set a timeout in case the extension doesn't respond
        const timeoutId = setTimeout(() => {
          if (!responseReceived) {
            setIsInstalled(false);
            setIsChecking(false);
          }
        }, 1000);

        // Try to send a message to the extension
        window.chrome.runtime.sendMessage(
          SHAARP_EXTENSION_ID,
          { type: "PING" },
          (response) => {
            responseReceived = true;
            clearTimeout(timeoutId);

            // Check if there was an error (extension not installed or not responding)
            if (window.chrome?.runtime?.lastError) {
              setIsInstalled(false);
            } else if (
              response &&
              typeof response === "object" &&
              "installed" in response
            ) {
              setIsInstalled(true);
            } else {
              setIsInstalled(false);
            }
            setIsChecking(false);
          }
        );
      } catch (error) {
        setIsInstalled(false);
        setIsChecking(false);
      }
    };

    checkExtension();
  }, []);

  return { isInstalled, isChecking };
}
